import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { HOSPITAL, NAV_LINKS, waLink } from "../data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header data-testid="site-header" className="sticky top-0 z-[60] border-b border-white/10 bg-navy/95 backdrop-blur-md">
      <div className="mx-auto flex min-h-16 max-w-7xl items-center justify-between gap-4 px-4 py-2 sm:px-6 lg:min-h-[72px] lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-lg shadow-navy-950/30 ring-1 ring-white/20">
            <img loading="lazy" decoding="async" src="/images/logo-mark.webp" alt="Shri Sai Chest and Allergy Hospital logo" className="h-full w-full object-contain" />
          </span>
          <span className="leading-tight">
            <span className="block whitespace-nowrap font-heading text-[11px] font-bold tracking-tight text-white min-[380px]:text-xs xl:text-sm">
              Shri Sai Chest and Allergy Hospital
            </span>
            <span className="hidden text-[8px] font-medium uppercase tracking-[0.06em] text-teal-300 min-[380px]:block sm:whitespace-nowrap sm:text-[9px] lg:hidden xl:block">
              Dr Amol Shembatwad Chest and Respiratory Clinic
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-2.5 py-2 text-xs font-medium transition-colors duration-200 2xl:px-3.5 2xl:text-[13px] ${
                  isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-call-btn"
            href={`tel:${HOSPITAL.phone}`}
            className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-white/10 px-4 py-2 text-[13px] font-semibold text-white ring-1 ring-white/15 transition-colors hover:bg-white/15 2xl:flex"
          >
            <Phone className="h-3.5 w-3.5" /> {HOSPITAL.phone}
          </a>
          <Link
            to="/book-appointment"
            data-testid="nav-book-btn"
            className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-brand px-3 py-2 text-xs font-semibold text-white shadow-lg shadow-teal-900/40 transition-all duration-200 hover:bg-brand-light sm:flex 2xl:px-4 2xl:text-[13px]"
          >
            <CalendarCheck className="h-3.5 w-3.5" /> Book Appointment
          </Link>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="rounded-lg p-2 text-white xl:hidden"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-navy xl:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-4 py-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-3 text-sm font-medium ${
                      isActive ? "bg-brand text-white" : "text-slate-200 hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex gap-3 pt-3">
                <a
                  data-testid="mobile-call-btn"
                  href={`tel:${HOSPITAL.phone}`}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white/10 py-3 text-sm font-semibold text-white ring-1 ring-white/15"
                >
                  <Phone className="h-4 w-4" /> Call Now
                </a>
                <a
                  data-testid="mobile-whatsapp-btn"
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-whatsapp py-3 text-sm font-semibold text-white"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
